import { useState, useRef, useEffect } from "react";
import {
    Upload, Loader2, FileDown, X, Car, Sparkles, ChevronDown,
} from "lucide-react";
import { api, API } from "../lib/api";
import { toast } from "sonner";

const DEFAULT_NLT = {
    client_name: "",
    client_surname: "",
    client_phone: "",
    marca: "",
    modello: "",
    tipologia: "nuovo_da_stock",
    description: "",
    monthly_price: "",
    list_price: "",
    anticipo: "",
    durata_mesi: 48,
    km_annui: "15.000",
    franchigia_rca: "€ 0",
    franchigia_kasko: "€ 0",
    franchigia_furto: "€ 0",
    servizi_inclusi: [],
    agent_name: "",
    agent_email: "",
    agent_phone: "",
};

export default function NltOfferGenerator() {
    const [presets, setPresets] = useState(null);
    const [form, setForm] = useState(DEFAULT_NLT);
    const [exteriorImg, setExteriorImg] = useState(null);
    const [interiorImg, setInteriorImg] = useState(null);
    const [generating, setGenerating] = useState(false);
    const extInput = useRef(null);
    const intInput = useRef(null);

    useEffect(() => {
        api.get("/admin/nlt-presets")
            .then(({ data }) => setPresets(data))
            .catch(() => toast.error("Errore caricamento presets"));
    }, []);

    const upload = async (file, which) => {
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data: res } = await api.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            const ref = { id: res.id, filename: res.filename, url: res.url };
            if (which === "exterior") setExteriorImg(ref);
            else setInteriorImg(ref);
            toast.success(`${which === "exterior" ? "Esterno" : "Interno"} caricato`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload fallito");
        }
    };

    const toggleService = (s) => {
        const cur = new Set(form.servizi_inclusi);
        if (cur.has(s)) cur.delete(s); else cur.add(s);
        setForm({ ...form, servizi_inclusi: [...cur] });
    };

    const generate = async () => {
        if (!form.marca.trim() || !form.modello.trim()) {
            toast.error("Marca e modello sono obbligatori");
            return;
        }
        setGenerating(true);
        try {
            const payload = {
                ...form,
                client_name: form.client_name.trim() || null,
                client_surname: form.client_surname.trim() || null,
                client_phone: form.client_phone.trim() || null,
                marca: form.marca.trim(),
                modello: form.modello.trim(),
                description: form.description.trim() || null,
                monthly_price: form.monthly_price.trim() || null,
                list_price: form.list_price.trim() || null,
                anticipo: form.anticipo.trim() || null,
                durata_mesi: Number(form.durata_mesi),
                km_annui: String(form.km_annui ?? "").trim(),
                exterior_image_file_id: exteriorImg?.id || null,
                interior_image_file_id: interiorImg?.id || null,
                agent_name: form.agent_name.trim() || null,
                agent_email: form.agent_email.trim() || null,
                agent_phone: form.agent_phone.trim() || null,
            };
            const resp = await api.post("/admin/nlt-offer-pdf", payload, {
                responseType: "blob", timeout: 45000,
            });
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `NLT-${form.marca}-${form.modello}.pdf`.replace(/\s+/g, "_");
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 800);
            toast.success("PDF generato");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore generazione");
        } finally {
            setGenerating(false);
        }
    };

    if (!presets) {
        return (
            <div className="text-center py-20 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="nlt-offer-form">
            {/* Sezione 1: Cliente */}
            <Section title="Dati cliente">
                <div className="grid sm:grid-cols-3 gap-3">
                    <F label="Nome" testid="nlt-client-name">
                        <input className="field"
                               value={form.client_name}
                               onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                               placeholder="Mario" />
                    </F>
                    <F label="Cognome" testid="nlt-client-surname">
                        <input className="field"
                               value={form.client_surname}
                               onChange={(e) => setForm({ ...form, client_surname: e.target.value })}
                               placeholder="Rossi" />
                    </F>
                    <F label="Telefono" testid="nlt-client-phone">
                        <input className="field"
                               value={form.client_phone}
                               onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                               placeholder="+39 333 1234567" />
                    </F>
                </div>
            </Section>

            {/* Sezione 2: Vettura */}
            <Section title="Vettura">
                <div className="grid sm:grid-cols-2 gap-3">
                    <F label="Marca *" testid="nlt-marca">
                        <input className="field"
                               value={form.marca}
                               onChange={(e) => setForm({ ...form, marca: e.target.value })}
                               placeholder="Volkswagen" />
                    </F>
                    <F label="Modello *" testid="nlt-modello">
                        <input className="field"
                               value={form.modello}
                               onChange={(e) => setForm({ ...form, modello: e.target.value })}
                               placeholder="Polo TGI 1.0 Style" />
                    </F>
                </div>
                <div className="mt-3">
                    <label className="label">Tipologia *</label>
                    <div className="grid sm:grid-cols-3 gap-2 mt-1">
                        {presets.tipologie.map((t) => {
                            const active = form.tipologia === t.value;
                            return (
                                <button key={t.value}
                                        type="button"
                                        onClick={() => setForm({ ...form, tipologia: t.value })}
                                        data-testid={`nlt-tipologia-${t.value}`}
                                        className={`text-left rounded-xl p-3 border-2 transition-all ${
                                            active
                                                ? "bg-[#0C4650] text-white border-[#0C4650]"
                                                : "bg-white text-[#0C4650] border-gray-200 hover:border-[#0C4650]"
                                        }`}>
                                    <Car className={`w-4 h-4 ${active ? "text-[#C7FF2C]" : "text-[#0C4650]"}`} />
                                    <div className="font-bold text-sm mt-1">{t.label}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="mt-3">
                    <F label="Descrizione" testid="nlt-desc">
                        <textarea rows={3} className="field"
                                  value={form.description}
                                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                                  placeholder="Optional, fino a 2000 caratteri" />
                    </F>
                </div>
            </Section>

            {/* Sezione 3: Condizioni contrattuali */}
            <Section title="Condizioni contrattuali">
                <div className="grid sm:grid-cols-2 gap-3">
                    <F label="Durata *" testid="nlt-durata">
                        <SelectWithChevron
                            value={form.durata_mesi}
                            onChange={(v) => setForm({ ...form, durata_mesi: Number(v) })}
                            options={presets.durata_mesi.map((m) => ({ value: m, label: `${m} mesi` }))}
                        />
                    </F>
                    <F label="Km annui *" testid="nlt-km">
                        <input className="field" type="text" maxLength={40}
                               value={form.km_annui}
                               onChange={(e) => setForm({ ...form, km_annui: e.target.value })}
                               placeholder="es. 15.000 km o ILLIMITATI" />
                    </F>
                    <F label="Franchigia RCA" testid="nlt-fr-rca">
                        <SelectWithChevron
                            value={form.franchigia_rca}
                            onChange={(v) => setForm({ ...form, franchigia_rca: v })}
                            options={presets.franchigia_rca.map((v) => ({ value: v, label: v }))}
                        />
                    </F>
                    <F label="Franchigia Kasko" testid="nlt-fr-kasko">
                        <SelectWithChevron
                            value={form.franchigia_kasko}
                            onChange={(v) => setForm({ ...form, franchigia_kasko: v })}
                            options={presets.franchigia_kasko.map((v) => ({ value: v, label: v }))}
                        />
                    </F>
                    <F label="Franchigia Furto/Incendio" testid="nlt-fr-furto">
                        <SelectWithChevron
                            value={form.franchigia_furto}
                            onChange={(v) => setForm({ ...form, franchigia_furto: v })}
                            options={presets.franchigia_furto.map((v) => ({ value: v, label: v }))}
                        />
                    </F>
                    <F label="Anticipo" testid="nlt-anticipo">
                        <input className="field"
                               value={form.anticipo}
                               onChange={(e) => setForm({ ...form, anticipo: e.target.value })}
                               placeholder="€ 1.500" />
                    </F>
                </div>
            </Section>

            {/* Sezione 4: Servizi inclusi */}
            <Section title="Servizi inclusi"
                     subtitle="Seleziona i servizi inclusi nel canone. Quelli non flaggati appariranno come 'non inclusi'.">
                <div className="grid sm:grid-cols-2 gap-2 mt-1" data-testid="nlt-services">
                    {presets.servizi_inclusi.map((s) => {
                        const checked = form.servizi_inclusi.includes(s);
                        return (
                            <label key={s}
                                   data-testid={`nlt-service-${s.replace(/[^a-z0-9]+/gi, "_").toLowerCase()}`}
                                   className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                       checked
                                           ? "border-[#C7FF2C] bg-[#C7FF2C]/15"
                                           : "border-gray-200 hover:border-gray-300"
                                   }`}>
                                <input type="checkbox" checked={checked}
                                       onChange={() => toggleService(s)}
                                       className="accent-[#0C4650] w-4 h-4 shrink-0" />
                                <span className="font-bold text-[#0C4650] text-sm">{s}</span>
                            </label>
                        );
                    })}
                </div>
            </Section>

            {/* Sezione 5: Prezzo */}
            <Section title="Prezzo">
                <div className="grid sm:grid-cols-2 gap-3">
                    <F label="Rata mensile" testid="nlt-monthly">
                        <input className="field"
                               value={form.monthly_price}
                               onChange={(e) => setForm({ ...form, monthly_price: e.target.value })}
                               placeholder="€ 285/mese" />
                    </F>
                    <F label="Listino di riferimento" testid="nlt-list">
                        <input className="field"
                               value={form.list_price}
                               onChange={(e) => setForm({ ...form, list_price: e.target.value })}
                               placeholder="€ 22.500" />
                    </F>
                </div>
            </Section>

            {/* Sezione 6: Immagini */}
            <Section title="Immagini"
                     subtitle="Foto esterno (banda hero) e foto interno (dettaglio sotto le condizioni).">
                <div className="grid sm:grid-cols-2 gap-3">
                    <ImageSlot
                        label="Foto esterno (hero)"
                        image={exteriorImg}
                        onUpload={(f) => upload(f, "exterior")}
                        onRemove={() => setExteriorImg(null)}
                        inputRef={extInput}
                        testid="nlt-img-ext"
                    />
                    <ImageSlot
                        label="Foto interno (dettaglio)"
                        image={interiorImg}
                        onUpload={(f) => upload(f, "interior")}
                        onRemove={() => setInteriorImg(null)}
                        inputRef={intInput}
                        testid="nlt-img-int"
                    />
                </div>
            </Section>

            {/* Sezione 7: Referente */}
            <Section title="Referente commerciale">
                <div className="grid sm:grid-cols-3 gap-3">
                    <F label="Nome" testid="nlt-ref-name">
                        <input className="field"
                               value={form.agent_name}
                               onChange={(e) => setForm({ ...form, agent_name: e.target.value })}
                               placeholder="Sara Bianchi" />
                    </F>
                    <F label="Email" testid="nlt-ref-email">
                        <input className="field"
                               value={form.agent_email}
                               onChange={(e) => setForm({ ...form, agent_email: e.target.value })}
                               placeholder="sara@scintilla.it" />
                    </F>
                    <F label="Telefono" testid="nlt-ref-phone">
                        <input className="field"
                               value={form.agent_phone}
                               onChange={(e) => setForm({ ...form, agent_phone: e.target.value })}
                               placeholder="+39 ..." />
                    </F>
                </div>
            </Section>

            {/* CTA */}
            <div className="flex justify-end pb-8">
                <button onClick={generate} disabled={generating}
                        className="btn-primary disabled:opacity-50"
                        data-testid="nlt-generate">
                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                    Genera e scarica PDF
                </button>
            </div>
        </div>
    );
}

const Section = ({ title, subtitle, children }) => (
    <div className="card p-5 sm:p-6">
        <h2 className="text-lg font-black text-[#0C4650]">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        <div className="mt-4">{children}</div>
    </div>
);

const F = ({ label, children, testid }) => (
    <div data-testid={testid}>
        <label className="label">{label}</label>
        {children}
    </div>
);

const SelectWithChevron = ({ value, onChange, options }) => (
    <div className="relative">
        <select className="field appearance-none pr-9"
                value={value}
                onChange={(e) => onChange(e.target.value)}>
            {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
        <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#0C4650] pointer-events-none" />
    </div>
);

const ImageSlot = ({ label, image, onUpload, onRemove, inputRef, testid }) => {
    const token = localStorage.getItem("scintilla_token") || "";
    const imgUrl = image?.url
        ? `${API}${image.url}${image.url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`
        : null;
    return (
        <div data-testid={testid}>
            <div className="text-xs font-bold text-[#0C4650] mb-1">{label}</div>
            {image ? (
                <div className="relative rounded-xl overflow-hidden h-36 bg-[#f7f7f5]">
                    {imgUrl && (
                        <div className="absolute inset-0 bg-cover bg-center"
                             style={{ backgroundImage: `url(${imgUrl})` }} />
                    )}
                    <button onClick={onRemove}
                            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white text-red-500 flex items-center justify-center shadow-md"
                            data-testid={`${testid}-remove`}>
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ) : (
                <label className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-[#0C4650]/30 hover:border-[#0C4650] rounded-xl py-9 cursor-pointer bg-[#f7f7f5]">
                    <Upload className="w-5 h-5 text-[#0C4650]" />
                    <span className="text-xs font-bold text-[#0C4650]">Carica</span>
                    <input ref={inputRef} type="file" accept="image/*" className="hidden"
                           onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
                           data-testid={`${testid}-input`} />
                </label>
            )}
        </div>
    );
};
